import {
  allocateBulkPaymentFifo,
  localTodayIso,
  sumOpenDebtRowTotals,
  UNASSIGNED_DEBT_OWNER_KEY,
  type CashbookDebtPaymentAllocation,
  type CashbookFundingSourcePostable,
  type OpenDebtRow
} from '@finvox/shared/cashbook';
import { parseMoneyInput, toMoney } from '@finvox/shared/money';
import { format, parseISO } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CashbookFundingSourcePicker } from '@/components/cashbook/CashbookFundingSourcePicker';
import { DebtOwnerSelect } from '@/components/cashbook/DebtOwnerSelect';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { Input } from '@/components/ui/Input';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import { MoneyInput } from '@/components/ui/MoneyInput';
import { cn } from '@/lib/cn';

type Props = {
  creditor: string;
  date: string;
  memo: string;
  fundingSource: CashbookFundingSourcePostable | '';
  openLines: OpenDebtRow[];
  payOwners: string[];
  /** Recorded left-in-drawer for today; null when not recorded yet. */
  leftInDrawer: string | null;
  busy: boolean;
  disabled: boolean;
  embedded?: boolean;
  onCancel?: () => void;
  onCreditorChange: (v: string) => void;
  onDateChange: (v: string) => void;
  onMemoChange: (v: string) => void;
  onFundingSourceChange: (v: CashbookFundingSourcePostable) => void;
  onPay: (amount: string) => void;
};

export function CashbookPayDebtForm({
  creditor,
  date,
  memo,
  fundingSource,
  openLines,
  payOwners,
  leftInDrawer,
  busy,
  disabled,
  embedded = false,
  onCancel,
  onCreditorChange,
  onDateChange,
  onMemoChange,
  onFundingSourceChange,
  onPay
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? ar : enUS;
  const [amountRaw, setAmountRaw] = useState('');

  useEffect(() => {
    setAmountRaw('');
  }, [creditor]);

  const ownerSelected = Boolean(creditor.trim());
  const ownerLabels = useMemo(
    () => ({
      [UNASSIGNED_DEBT_OWNER_KEY]: t('cashbook.debts.unassignedOpeningDebt')
    }),
    [t]
  );

  const totalRemaining = useMemo(
    () => sumOpenDebtRowTotals(openLines).remainingAmount,
    [openLines]
  );

  const amount = parseMoneyInput(amountRaw || '0');
  const amountPositive = toMoney(amount).gt(0);
  const overpay =
    amountPositive && toMoney(amount).gt(toMoney(totalRemaining));

  const previewAllocations: CashbookDebtPaymentAllocation[] = useMemo(() => {
    if (!amountPositive || overpay || openLines.length === 0) return [];
    try {
      return allocateBulkPaymentFifo(openLines, amount);
    } catch {
      return [];
    }
  }, [amount, amountPositive, openLines, overpay]);

  const allocById = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of previewAllocations) map.set(a.debtEntryId, a.amount);
    return map;
  }, [previewAllocations]);

  const drawerBlocked =
    fundingSource === 'drawer' &&
    leftInDrawer != null &&
    amountPositive &&
    toMoney(amount).gt(toMoney(leftInDrawer));

  const canPay =
    !disabled &&
    ownerSelected &&
    amountPositive &&
    !overpay &&
    !drawerBlocked &&
    openLines.length > 0 &&
    (fundingSource === 'drawer' || fundingSource === 'treasury');

  function fillAllRemaining() {
    setAmountRaw(totalRemaining);
  }

  return (
    <div
      className={cn(
        embedded
          ? 'grid grid-cols-1 gap-4'
          : 'mt-4 flex flex-col gap-4 rounded-lg border border-[var(--fv-border)] bg-[var(--fv-surface)] p-4'
      )}
    >
      {!embedded ? (
        <p className="w-full text-sm font-medium text-[var(--fv-text)]">
          {t('cashbook.debts.payTitle')}
        </p>
      ) : null}
      <p className="w-full text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.payOpenHint')}</p>
      <div className="w-full">
        <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.creditor')}</label>
        <DebtOwnerSelect
          className="mt-1 w-full"
          value={creditor}
          onValueChange={onCreditorChange}
          owners={payOwners}
          ownerLabels={ownerLabels}
          allowCreate={false}
          disabled={disabled || payOwners.length === 0}
          placeholder={t('cashbook.debts.selectOwner')}
        />
      </div>

      {ownerSelected && openLines.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto] sm:items-end">
            <div className="w-full">
              <label className="text-xs text-[var(--fv-text-muted)]">
                {t('cashbook.debts.payAmount')}
              </label>
              <MoneyInput
                className={cn('mt-1 w-full', overpay && 'border-[var(--fv-danger)]')}
                allowEmpty
                value={amountRaw}
                onValueChange={setAmountRaw}
                disabled={disabled}
                aria-label={t('cashbook.debts.payAmount')}
              />
            </div>
            <Button type="button" variant="secondary" disabled={disabled} onClick={fillAllRemaining}>
              {t('cashbook.debts.payAllRemaining')}
            </Button>
          </div>
          <p className="text-xs text-[var(--fv-text-muted)]">
            {t('cashbook.debts.payRemainingTotal')}:{' '}
            <span className="tabular-nums font-medium text-[var(--fv-text)]">
              <MoneyDisplay value={totalRemaining} />
            </span>
          </p>
          <p className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.payFifoHint')}</p>
          <div className="max-h-72 overflow-auto rounded-lg border border-[var(--fv-border)]">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-[1] text-[var(--fv-text-muted)]">
                <tr>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.colDate')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.category')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-start font-medium">{t('cashbook.memo')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colRemaining')}</th>
                  <th className="bg-[var(--fv-bg)] px-3 py-2 text-end font-medium">{t('cashbook.debts.colApplied')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--fv-border)]">
                {openLines.map((row) => {
                  const applied = allocById.get(row.id);
                  return (
                    <tr key={row.id}>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {format(parseISO(row.date), 'PP', { locale })}
                      </td>
                      <td className="px-3 py-2">
                        {row.source === 'opening_debt'
                          ? t('cashbook.debts.openingRow')
                          : t(`cashbook.debts.categories.${row.categoryKey}`, {
                              defaultValue: row.categoryKey
                            })}
                      </td>
                      <td className="px-3 py-2 text-[var(--fv-text-muted)]">{row.memo ?? '—'}</td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        <MoneyDisplay value={row.remainingAmount} />
                      </td>
                      <td className="px-3 py-2 text-end tabular-nums">
                        {applied ? <MoneyDisplay value={applied} /> : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 z-[1]">
                <tr className="border-t border-[var(--fv-border)] font-medium shadow-[0_-6px_12px_-8px_rgb(15_23_42_/_0.18)]">
                  <td className="bg-[var(--fv-bg)] px-3 py-2" colSpan={4}>
                    {t('cashbook.debts.payTotal')}
                  </td>
                  <td className="bg-[var(--fv-bg)] px-3 py-2 text-end tabular-nums">
                    <MoneyDisplay value={amountPositive && !overpay ? amount : '0.00'} />
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div className="w-full">
          <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.colDate')}</label>
          <DatePicker
            className="mt-1 w-full"
            value={date}
            onChange={onDateChange}
            disabled={disabled}
            max={localTodayIso()}
          />
        </div>
        <div className="w-full md:col-span-2">
          <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.memo')}</label>
          <Input
            className="mt-1 w-full"
            value={memo}
            onChange={(e) => onMemoChange(e.target.value)}
            disabled={disabled}
            aria-label={t('cashbook.memo')}
          />
        </div>
        <div className="w-full md:col-span-2">
          <CashbookFundingSourcePicker
            value={fundingSource}
            onChange={onFundingSourceChange}
            disabled={disabled}
            required
          />
        </div>
      </div>

      {overpay ? (
        <p className="w-full text-xs text-[var(--fv-danger)]">
          {t('cashbook.debts.errors.overpaymentOwner', { remaining: totalRemaining })}
        </p>
      ) : null}
      {drawerBlocked ? (
        <p className="w-full text-xs text-[var(--fv-danger)]">
          {t('cashbook.debts.errors.exceedsDrawer', { leftInDrawer })}
        </p>
      ) : null}
      {payOwners.length === 0 ? (
        <p className="w-full text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.detailEmpty')}</p>
      ) : null}
      {ownerSelected && openLines.length === 0 ? (
        <p className="w-full text-xs text-[var(--fv-text-muted)]">{t('cashbook.debts.detailEmpty')}</p>
      ) : null}

      <div className="flex w-full justify-end gap-2">
        {embedded && onCancel ? (
          <Button variant="secondary" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button loading={busy} disabled={!canPay} onClick={() => onPay(amount)}>
          {t('cashbook.debts.payButton')}
        </Button>
      </div>
    </div>
  );
}
